import { ContactItem } from '@AMIEWEB/Common';
import { IDisplayInternalContact, IUpdateInternalContactCommand } from '@AMIEWEB/Facility/FacilityStore/Facility.model';
import { facilityActions } from '@AMIEWEB/Facility/FacilityStore/Facility.redux';
import {
  selectEditFacilityInternalContacts,
  selectFacility,
  selectFacilityInternalContacts,
} from '@AMIEWEB/Facility/FacilityStore/Facility.selector';
import { getInitials } from '@AMIEWEB/Placement/PlacementDetails/PlacementDetailsHeader/utils';
import { Divider, List, Typography } from '@mui/material';
import { missingField } from 'app/constants';
import { getSearchLookups } from 'app/services/SharedServices/SharedServices';
import { selectUser } from 'oidc/user.selectors';
import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { InternalContactSaveModal } from './InternalContactSaveModal';
import { ContactCheckbox } from '@AMIEWEB/Placement/PlacementDetails/PlacementDetailsHeader/helper';
import { SelectAllCheckbox } from '@AMIEWEB/Common/ContactsDrawer/ContactList';
import { IContactEditor } from 'app/models/Placement/PlacementDetails';
import { ContactEditor, KeyContacts } from './helper';

const useStyles = makeStyles()(theme => ({
  root: {
    padding: '0px 5px',
  },
  disabled: {
    WebkitTextFillColor: `${theme.palette.framework.system.charcoal} !important`,
  },
  smallTextfield: {
    paddingTop: '0 !important',
    '&.Mui-disabled': {
      '&:before': {
        borderBottom: `1px solid rgba(0, 0, 0, 0.42) !important`,
      },
      '&:hover, &:after': {
        borderBottom: `1px solid ${theme.palette.system.fadedGrey} !important`,
      },
      '&:hover:before': {
        borderBottom: 'none !important',
      },
    },
  },
  option: {
    '&.MuiAutocomplete-option[data-option-index="0"]': {
      fontStyle: 'italic',
    },
  },
}));

interface ISaveInternalContacts {
  id: string;
  key: string;
  name: string;
  value: string;
  employeeRole: string;
}

/**
 * Component to render Internal contacts for view/edit
 */
interface IInternalContactsProps {
  disableEmailVoiceIcon: boolean;
  setScrollPosition: () => void;
}
export const InternalContacts = (props: IInternalContactsProps) => {
  const { classes } = useStyles();
  const { disableEmailVoiceIcon, setScrollPosition } = props;
  const dispatch = useDispatch();
  const userDetails = useSelector(selectUser);
  const facilityData = useSelector(selectFacility);

  const {
    accountCoordinator,
    accountManager,
    billingSpecialist,
    clientAcctgRep,
    clientContractAdmin,
    clientContractSpecialist,
    clinicalDirector,
    credentialingAnalyst,
    credentialingSpecialist,
    customerSupportSpecialist,
    housingAccountExecutive,
    payrollRegion,
    payrollSpecialist,
    regionalDirector,
    timeProcessingSpecialist,
  } = useSelector(selectFacilityInternalContacts);

  const {
    accountManager: editAccountManager,
    accountCoordinator: editAccountCoordinator,
    clientContractSpecialist: editClientContractSpecialist,
    clinicalDirector: editClinicalDirector,
    credentialingAnalyst: editCredentialingAnalyst,
    credentialingSpecialist: editCredentialingSpecialist,
    customerSupportSpecialist: editCustomerSupportSpecialist,
    housingAccountExecutive: editHousingAccountExecutive,
    payrollRegion: editPayrollRegion,
    payrollSpecialist: editPayrollSpecialist,
    regionalDirector: editRegionalDirector,
    timeProcessingSpecialist: editTimeProcessingSpecialist,
    billingSpecialist: editBillingSpecialist,
    clientAcctgRep: editClientAcctgRep,
    clientContractAdmin: editClientContractAdmin,
  } = useSelector(selectEditFacilityInternalContacts);

  const editorInternalContacts = useSelector(selectEditFacilityInternalContacts);
  const keyInternalPartners = useSelector(selectFacilityInternalContacts);

  /**
   * Method to prevent lookup search call if last character is special character
   * @param searchKey
   */
  const ignoreLookupSearchForSpecialCharacter = (searchKey: string) => {
    const lastCharacter = searchKey.slice(-1);
    const specialCharacterPattern = /[!@#$%^&*()_+[\]{};:'"\\|,.<>/?]/;
    return specialCharacterPattern.test(lastCharacter);
  };

  /**
   * Method which trigger on searching in type ahead dropdown
   * @param field
   * @param key
   */
  const fetchOptions = async (field: string, key: string) => {
    if (ignoreLookupSearchForSpecialCharacter(key)) return;
    const options = await getSearchLookups(field, key);
    if (field != 'amieham')
      options.unshift({
        id: null,
        name: 'None',
      });
    return options;
  };

  /**
   * Method to trigger on change of the type ahead
   * @param key
   * @param value
   */
  const onChange = (key: string, value: { id: string; name: string; value: string }) => {
    value.name = value.name != 'None' ? value.name : '';
    setScrollPosition();
    dispatch(
      facilityActions.editInternalContact({
        key,
        data: {
          ...editorInternalContacts[key],
          value,
          error: null,
          isDirty: true,
        },
      }),
    );
  };

  /**
   * Method to handle save on clicking check icon
   * @param data
   */
  const handleSaveContact = (data: ISaveInternalContacts) => {
    let key = '';
    let keyContact = 0;
    var userName = data.name.split(' ');

    const firstName = userName[0];
    const lastName = userName[1] ? userName[1] : '';
    const keyValue = data.key === undefined ? data.employeeRole : data.key;

    switch (keyValue) {
      case KeyContacts.AccountManager:
        key = 'Account Manager';
        keyContact = 1;
        break;
      case KeyContacts.RegionalDirector:
        key = 'Regional Director';
        keyContact = 2;
        break;
      case KeyContacts.HousingManager:
        key = 'Housing Account Executive';
        keyContact = 3;
        break;
      case KeyContacts.BillingSpecialist:
        key = 'Billing Specialist';
        keyContact = 4;
        break;
      case KeyContacts.ClientAccountRepresentative:
        key = 'Client Acctg. Rep';
        keyContact = 5;
        break;
      case KeyContacts.TimeProcessingSpecialist:
        key = 'Time Processing Specialist';
        keyContact = 6;
        break;
      case KeyContacts.PayrollSpecialist:
        key = 'Payroll Specialist';
        keyContact = 7;
        break;
      case KeyContacts.PayrollRegion:
        key = 'Payroll Region';
        keyContact = 8;
        break;
      case KeyContacts.ClientContractAdmin:
        key = 'Client Contract Admin';
        keyContact = 9;
        break;
      case KeyContacts.ClientContractSpecialist:
        key = 'Client Contract Specialist';
        keyContact = 10;
        break;
      case KeyContacts.QSAnalyst:
        key = 'Credentialing Analyst';
        keyContact = 11;
        break;
      case KeyContacts.ClinicalLiaison:
        key = 'Clinical Director';
        keyContact = 12;
        break;
      case KeyContacts.HousingAccountingSpecialist:
        key = 'Credentialing Specialist';
        keyContact = 13;
        break;
      case KeyContacts.AccountCoordinator:
        key = 'Account Coordinator';
        keyContact = 14;
        break;
      case KeyContacts.CustomerSupportSpecialist:
        key = 'Customer Support Specialist';
        keyContact = 15;
        break;
      default:
        break;
    }

    const updatedInternalContactDetail: IUpdateInternalContactCommand = {
      facilityId: facilityData.facilityResponse.facilityId,
      key: key,
      keyContact: keyContact,
      userID: parseInt(data.id),
      userRoleName: data.name,
      isParentUpdateRequired: false,
      currentEmployeeId: userDetails.userInfo.employeeId,
      currentEmployeeName: userDetails.userInfo?.firstName + ' ' + userDetails.userInfo?.lastName,
    };

    const displayInternalContact: IDisplayInternalContact = {
      ...keyInternalPartners[keyValue],
      id: parseInt(data.id),
      employeeRole: keyValue,
      designation: key,
      keyAttribute: keyValue,
      avatarText:
        keyValue === KeyContacts.PayrollRegion
          ? firstName
            ? getInitials('Payroll', 'Region')
            : missingField
          : firstName && lastName
          ? getInitials(firstName, lastName).toUpperCase()
          : missingField,
      title: firstName + ' ' + lastName,
    };

    if (keyValue === KeyContacts.AccountManager || keyValue === KeyContacts.RegionalDirector) {
      dispatch(
        facilityActions.setSaveModalState({
          open: true,
          data: { displayInternalContact, updatedInternalContactDetail },
        }),
      );
    } else {
      dispatch(
        facilityActions.requestUpdateInternalContact({
          updateInternalContact: updatedInternalContactDetail,
          setInternalContact: displayInternalContact,
        }),
      );
    }
  };

  /**
   * Method to reset facility role value on clicking cancel
   * @param facilityRole
   * @returns
   */
  const resetInternalContactValue = (facilityRole: string) => {
    switch (facilityRole) {
      case 'accountCoordinator':
        return accountCoordinator;
      case 'accountManager':
        return accountManager;
      case 'clientContractSpecialist':
        return clientContractSpecialist;
      case 'clinicalDirector':
        return clinicalDirector;
      case 'credentialingAnalyst':
        return credentialingAnalyst;
      case 'credentialingSpecialist':
        return credentialingSpecialist;
      case 'customerSupportSpecialist':
        return customerSupportSpecialist;
      case 'housingAccountExecutive':
        return housingAccountExecutive;
      case 'payrollRegion':
        return payrollRegion;
      case 'payrollSpecialist':
        return payrollSpecialist;
      case 'regionalDirector':
        return regionalDirector;
      case 'timeProcessingSpecialist':
        return timeProcessingSpecialist;
    }
  };

  /**
   * Method to trigger on clicking close icon
   * @param key
   */
  const onCancel = (key: string) => {
    const field = resetInternalContactValue(key);
    dispatch(
      facilityActions.editInternalContact({
        key,
        data: {
          ...editorInternalContacts[key],
          value: field.id ? { name: field.title, value: field.id, id: field.id } : null,
          error: null,
          isDirty: false,
        },
      }),
    );
  };

  const _accountManager = React.useMemo(
    () => (
      <ContactEditor
        {...accountManager}
        {...editAccountManager}
        applyNoneOptionStyle={false}
        classes={classes}
        fetchOptions={key => fetchOptions('amieham', key)}
        onApply={handleSaveContact}
        onChange={newValue => onChange('accountManager', newValue)}
        onCancel={() => onCancel('accountManager')}
      />
    ),
    [accountManager, editAccountManager],
  );

  const _accountCoordinator = React.useMemo(
    () => (
      <ContactEditor
        {...accountCoordinator}
        {...editAccountCoordinator}
        classes={classes}
        fetchOptions={key => fetchOptions('amieac', key)}
        onApply={handleSaveContact}
        onChange={newValue => onChange('accountCoordinator', newValue)}
        onCancel={() => onCancel('accountCoordinator')}
      />
    ),
    [accountCoordinator, editAccountCoordinator],
  );

  const _clientContractSpecialist = React.useMemo(
    () => (
      <ContactEditor
        {...clientContractSpecialist}
        {...editClientContractSpecialist}
        classes={classes}
        fetchOptions={key => fetchOptions('amieccs', key)}
        onApply={handleSaveContact}
        onChange={newValue => onChange('clientContractSpecialist', newValue)}
        onCancel={() => onCancel('clientContractSpecialist')}
      />
    ),
    [clientContractSpecialist, editClientContractSpecialist],
  );

  const _clinicalDirector = React.useMemo(
    () => (
      <ContactEditor
        {...clinicalDirector}
        {...editClinicalDirector}
        classes={classes}
        fetchOptions={key => fetchOptions('amiecl', key)}
        onApply={handleSaveContact}
        onChange={newValue => onChange('clinicalDirector', newValue)}
        onCancel={() => onCancel('clinicalDirector')}
      />
    ),
    [clinicalDirector, editClinicalDirector],
  );

  const _credentialingAnalyst = React.useMemo(
    () => (
      <ContactEditor
        {...credentialingAnalyst}
        {...editCredentialingAnalyst}
        classes={classes}
        fetchOptions={key => fetchOptions('amieqsa', key)}
        onApply={handleSaveContact}
        onChange={newValue => onChange('credentialingAnalyst', newValue)}
        onCancel={() => onCancel('credentialingAnalyst')}
      />
    ),
    [credentialingAnalyst, editCredentialingAnalyst],
  );

  const _credentialingSpecialist = React.useMemo(
    () => (
      <ContactEditor
        {...credentialingSpecialist}
        {...editCredentialingSpecialist}
        classes={classes}
        fetchOptions={key => fetchOptions('amiehas', key)}
        onApply={handleSaveContact}
        onChange={newValue => onChange('credentialingSpecialist', newValue)}
        onCancel={() => onCancel('credentialingSpecialist')}
      />
    ),
    [credentialingSpecialist, editCredentialingSpecialist],
  );

  const _customerSupportSpecialist = React.useMemo(
    () => (
      <ContactEditor
        {...customerSupportSpecialist}
        {...editCustomerSupportSpecialist}
        classes={classes}
        fetchOptions={key => fetchOptions('amiecs', key)}
        onApply={handleSaveContact}
        onChange={newValue => onChange('customerSupportSpecialist', newValue)}
        onCancel={() => onCancel('customerSupportSpecialist')}
      />
    ),
    [customerSupportSpecialist, editCustomerSupportSpecialist],
  );

  const _housingAccountExecutive = React.useMemo(
    () => (
      <ContactEditor
        {...housingAccountExecutive}
        {...editHousingAccountExecutive}
        classes={classes}
        fetchOptions={key => fetchOptions('amiehm', key)}
        onApply={handleSaveContact}
        onChange={newValue => onChange('housingAccountExecutive', newValue)}
        onCancel={() => onCancel('housingAccountExecutive')}
      />
    ),
    [housingAccountExecutive, editHousingAccountExecutive],
  );

  const _payrollRegion = React.useMemo(
    () => (
      <ContactEditor
        {...payrollRegion}
        {...editPayrollRegion}
        classes={classes}
        fetchOptions={key => fetchOptions('payrollregion', key)}
        onApply={handleSaveContact}
        onChange={newValue => onChange('payrollRegion', newValue)}
        onCancel={() => onCancel('payrollRegion')}
      />
    ),
    [payrollRegion, editPayrollRegion],
  );

  const _payrollSpecialist = React.useMemo(
    () => (
      <ContactEditor
        {...payrollSpecialist}
        {...editPayrollSpecialist}
        classes={classes}
        fetchOptions={key => fetchOptions('amieps', key)}
        onApply={handleSaveContact}
        onChange={newValue => onChange('payrollSpecialist', newValue)}
        onCancel={() => onCancel('payrollSpecialist')}
      />
    ),
    [payrollSpecialist, editPayrollSpecialist],
  );

  const _regionalDirector = React.useMemo(
    () => (
      <ContactEditor
        {...regionalDirector}
        {...editRegionalDirector}
        classes={classes}
        fetchOptions={key => fetchOptions('amierd', key)}
        onApply={handleSaveContact}
        onChange={newValue => onChange('regionalDirector', newValue)}
        onCancel={() => onCancel('regionalDirector')}
      />
    ),
    [regionalDirector, editRegionalDirector],
  );

  const _timeProcessingSpecialist = React.useMemo(
    () => (
      <ContactEditor
        {...timeProcessingSpecialist}
        {...editTimeProcessingSpecialist}
        classes={classes}
        fetchOptions={key => fetchOptions('amietps', key)}
        onApply={handleSaveContact}
        onChange={newValue => onChange('timeProcessingSpecialist', newValue)}
        onCancel={() => onCancel('timeProcessingSpecialist')}
      />
    ),
    [timeProcessingSpecialist, editTimeProcessingSpecialist],
  );

  // Method to handle checkbox click
  const onCheckboxClick = (key: string, contact: IContactEditor) => {
    setScrollPosition();
    dispatch(
      facilityActions.editInternalContact({
        key,
        data: {
          ...editorInternalContacts[key],
          checked: contact.checked,
        },
      }),
    );
  };

  // Method to handle select all
  const handleSelectAllContacts = () => {
    setScrollPosition();
    const keyInternalContacts = Object.fromEntries(
      Object.entries(editorInternalContacts)?.map(([key, entry]) => [
        key,
        { ...entry, checked: !entry?.isDirty && keyInternalPartners[key]?.email ? !isAllContactsSelected() : false },
      ]),
    );
    // @ts-ignore: unspecified object type
    dispatch(facilityActions.setEditInternalContacts(keyInternalContacts));
  };

  const isAllContactsSelected = () => {
    let selectContacts = Object.fromEntries(
      Object.entries(editorInternalContacts)?.filter(([key, value]) => {
        return !value?.isDirty && keyInternalPartners[key]?.email;
      }),
    );
    if (selectContacts && Object.values(selectContacts)?.length === 0) {
      return false;
    } else {
      return selectContacts && Object.values(selectContacts)?.every(value => value?.checked);
    }
  };

  // Method to check if all contacts are selected
  const selectAll = useMemo(() => isAllContactsSelected(), [editorInternalContacts]);

  return (
    <>
      <List className={classes.root}>
        <SelectAllCheckbox checked={selectAll} disabled={false} handleSelectAll={handleSelectAllContacts} />
        <ContactItem
          {...accountCoordinator}
          title={_accountCoordinator}
          enableCommunication
          disableMailIcon={editAccountCoordinator?.isDirty || disableEmailVoiceIcon || false}
          disablePhoneIcon={editAccountCoordinator?.isDirty || disableEmailVoiceIcon || false}
          disableMessageIcon={editAccountCoordinator?.isDirty || false}
          disableAvatar={editAccountCoordinator?.isDirty || !editAccountCoordinator?.value || false}
          showCheckbox
          checkbox={
            <ContactCheckbox
              contacts={accountCoordinator}
              editContacts={editAccountCoordinator}
              onValueChange={onCheckboxClick}
              disabled={editAccountCoordinator?.isDirty || !accountCoordinator?.email || false}
            />
          }
        />
        <Divider variant="fullWidth" component="li" />
        <ContactItem
          {...accountManager}
          title={_accountManager}
          enableCommunication
          disableMailIcon={editAccountManager?.isDirty || disableEmailVoiceIcon || false}
          disablePhoneIcon={editAccountManager?.isDirty || disableEmailVoiceIcon || false}
          disableMessageIcon={editAccountManager?.isDirty || false}
          disableAvatar={editAccountManager?.isDirty || !editAccountManager?.value || false}
          showCheckbox
          checkbox={
            <ContactCheckbox
              contacts={accountManager}
              editContacts={editAccountManager}
              onValueChange={onCheckboxClick}
              disabled={editAccountManager?.isDirty || !accountManager?.email || false}
            />
          }
        />
        <Divider variant="fullWidth" component="li" />
        <ContactItem
          {...billingSpecialist}
          showCheckbox
          enableCommunication
          disableMailIcon={disableEmailVoiceIcon}
          disablePhoneIcon={disableEmailVoiceIcon}
          checkbox={
            <ContactCheckbox
              contacts={billingSpecialist}
              editContacts={editBillingSpecialist}
              onValueChange={onCheckboxClick}
              disabled={editBillingSpecialist?.isDirty || !billingSpecialist?.email || false}
            />
          }
        />
        <Divider variant="fullWidth" component="li" />
        <ContactItem
          {...clientAcctgRep}
          showCheckbox
          enableCommunication
          disableMailIcon={disableEmailVoiceIcon}
          disablePhoneIcon={disableEmailVoiceIcon}
          checkbox={
            <ContactCheckbox
              contacts={clientAcctgRep}
              editContacts={editClientAcctgRep}
              onValueChange={onCheckboxClick}
              disabled={editClientAcctgRep?.isDirty || !clientAcctgRep?.email || false}
            />
          }
        />
        <Divider variant="fullWidth" component="li" />
        <ContactItem
          {...clientContractAdmin}
          enableCommunication
          disableMailIcon={disableEmailVoiceIcon}
          disablePhoneIcon={disableEmailVoiceIcon}
          showCheckbox
          checkbox={
            <ContactCheckbox
              contacts={clientContractAdmin}
              editContacts={editClientContractAdmin}
              onValueChange={onCheckboxClick}
              disabled={editClientContractAdmin?.isDirty || !clientContractAdmin?.email || false}
            />
          }
        />
        <Divider variant="fullWidth" component="li" />

        <ContactItem
          {...clientContractSpecialist}
          title={_clientContractSpecialist}
          enableCommunication
          disableMailIcon={editClientContractSpecialist?.isDirty || disableEmailVoiceIcon || false}
          disablePhoneIcon={editClientContractSpecialist?.isDirty || disableEmailVoiceIcon || false}
          disableMessageIcon={editClientContractSpecialist?.isDirty || false}
          disableAvatar={editClientContractSpecialist?.isDirty || !editClientContractSpecialist?.value || false}
          showCheckbox
          checkbox={
            <ContactCheckbox
              contacts={clientContractSpecialist}
              editContacts={editClientContractSpecialist}
              onValueChange={onCheckboxClick}
              disabled={editClientContractSpecialist?.isDirty || !clientContractSpecialist?.email || false}
            />
          }
        />
        <Divider variant="fullWidth" component="li" />
        <ContactItem
          {...clinicalDirector}
          title={_clinicalDirector}
          enableCommunication
          disableMailIcon={editClinicalDirector?.isDirty || disableEmailVoiceIcon || false}
          disablePhoneIcon={editClinicalDirector?.isDirty || disableEmailVoiceIcon || false}
          disableMessageIcon={editClinicalDirector?.isDirty || false}
          disableAvatar={editClinicalDirector?.isDirty || !editClinicalDirector?.value || false}
          showCheckbox
          checkbox={
            <ContactCheckbox
              contacts={clinicalDirector}
              editContacts={editClinicalDirector}
              onValueChange={onCheckboxClick}
              disabled={editClinicalDirector?.isDirty || !clinicalDirector?.email || false}
            />
          }
        />
        <Divider variant="fullWidth" component="li" />
        <ContactItem
          {...credentialingAnalyst}
          title={_credentialingAnalyst}
          enableCommunication
          disableMailIcon={editCredentialingAnalyst?.isDirty || disableEmailVoiceIcon || false}
          disablePhoneIcon={editCredentialingAnalyst?.isDirty || disableEmailVoiceIcon || false}
          disableMessageIcon={editCredentialingAnalyst?.isDirty || false}
          disableAvatar={editCredentialingAnalyst?.isDirty || !editCredentialingAnalyst?.value || false}
          showCheckbox
          checkbox={
            <ContactCheckbox
              contacts={credentialingAnalyst}
              editContacts={editCredentialingAnalyst}
              onValueChange={onCheckboxClick}
              disabled={editCredentialingAnalyst?.isDirty || !credentialingAnalyst?.email || false}
            />
          }
        />
        <Divider variant="fullWidth" component="li" />
        <ContactItem
          {...credentialingSpecialist}
          title={_credentialingSpecialist}
          enableCommunication
          disableMailIcon={editCredentialingSpecialist?.isDirty || disableEmailVoiceIcon || false}
          disablePhoneIcon={editCredentialingSpecialist?.isDirty || disableEmailVoiceIcon || false}
          disableMessageIcon={editCredentialingSpecialist?.isDirty || false}
          disableAvatar={editCredentialingSpecialist?.isDirty || !editCredentialingSpecialist?.value || false}
          showCheckbox
          checkbox={
            <ContactCheckbox
              contacts={credentialingSpecialist}
              editContacts={editCredentialingSpecialist}
              onValueChange={onCheckboxClick}
              disabled={editCredentialingSpecialist?.isDirty || !credentialingSpecialist?.email || false}
            />
          }
        />
        <Divider variant="fullWidth" component="li" />
        <ContactItem
          {...customerSupportSpecialist}
          title={_customerSupportSpecialist}
          enableCommunication
          disableMailIcon={editCustomerSupportSpecialist?.isDirty || disableEmailVoiceIcon || false}
          disablePhoneIcon={editCustomerSupportSpecialist?.isDirty || disableEmailVoiceIcon || false}
          disableMessageIcon={editCustomerSupportSpecialist?.isDirty || false}
          disableAvatar={editCustomerSupportSpecialist?.isDirty || !editCustomerSupportSpecialist?.value || false}
          showCheckbox
          checkbox={
            <ContactCheckbox
              contacts={customerSupportSpecialist}
              editContacts={editCustomerSupportSpecialist}
              onValueChange={onCheckboxClick}
              disabled={editCustomerSupportSpecialist?.isDirty || !customerSupportSpecialist?.email || false}
            />
          }
        />
        <Divider variant="fullWidth" component="li" />
        <ContactItem
          {...housingAccountExecutive}
          title={_housingAccountExecutive}
          enableCommunication
          disableMailIcon={editHousingAccountExecutive?.isDirty || disableEmailVoiceIcon || false}
          disablePhoneIcon={editHousingAccountExecutive?.isDirty || disableEmailVoiceIcon || false}
          disableMessageIcon={editHousingAccountExecutive?.isDirty || false}
          disableAvatar={editHousingAccountExecutive?.isDirty || !editHousingAccountExecutive?.value || false}
          showCheckbox
          checkbox={
            <ContactCheckbox
              contacts={housingAccountExecutive}
              editContacts={editHousingAccountExecutive}
              onValueChange={onCheckboxClick}
              disabled={editHousingAccountExecutive?.isDirty || !housingAccountExecutive?.email || false}
            />
          }
        />
        <Divider variant="fullWidth" component="li" />
        <ContactItem
          {...payrollRegion}
          title={_payrollRegion}
          enableCommunication
          disableMailIcon={editPayrollRegion?.isDirty || disableEmailVoiceIcon || false}
          disablePhoneIcon={editPayrollRegion?.isDirty || disableEmailVoiceIcon || false}
          disableMessageIcon={editPayrollRegion?.isDirty || false}
          disableAvatar={editPayrollRegion?.isDirty || !editPayrollRegion?.value || false}
          showCheckbox
          checkbox={
            <ContactCheckbox
              contacts={payrollRegion}
              editContacts={editPayrollRegion}
              onValueChange={onCheckboxClick}
              disabled={editPayrollRegion?.isDirty || !payrollRegion?.email || false}
            />
          }
        />
        <Divider variant="fullWidth" component="li" />

        <ContactItem
          {...payrollSpecialist}
          title={_payrollSpecialist}
          enableCommunication
          disableMailIcon={editPayrollSpecialist?.isDirty || disableEmailVoiceIcon || false}
          disablePhoneIcon={editPayrollSpecialist?.isDirty || disableEmailVoiceIcon || false}
          disableMessageIcon={editPayrollSpecialist?.isDirty || false}
          disableAvatar={editPayrollSpecialist?.isDirty || !editPayrollSpecialist?.value || false}
          showCheckbox
          checkbox={
            <ContactCheckbox
              contacts={payrollSpecialist}
              editContacts={editPayrollSpecialist}
              onValueChange={onCheckboxClick}
              disabled={editPayrollSpecialist?.isDirty || !payrollSpecialist?.email || false}
            />
          }
        />
        <Divider variant="fullWidth" component="li" />
        <ContactItem
          {...regionalDirector}
          title={_regionalDirector}
          enableCommunication
          disableMailIcon={editRegionalDirector?.isDirty || disableEmailVoiceIcon || false}
          disablePhoneIcon={editRegionalDirector?.isDirty || disableEmailVoiceIcon || false}
          disableMessageIcon={editRegionalDirector?.isDirty || false}
          disableAvatar={editRegionalDirector?.isDirty || !editRegionalDirector?.value || false}
          showCheckbox
          checkbox={
            <ContactCheckbox
              contacts={regionalDirector}
              editContacts={editRegionalDirector}
              onValueChange={onCheckboxClick}
              disabled={editRegionalDirector?.isDirty || !regionalDirector?.email || false}
            />
          }
        />
        <Divider variant="fullWidth" component="li" />

        <ContactItem
          {...timeProcessingSpecialist}
          title={_timeProcessingSpecialist}
          enableCommunication
          disableMailIcon={editTimeProcessingSpecialist?.isDirty || disableEmailVoiceIcon || false}
          disablePhoneIcon={editTimeProcessingSpecialist?.isDirty || disableEmailVoiceIcon || false}
          disableMessageIcon={editTimeProcessingSpecialist?.isDirty || false}
          disableAvatar={editTimeProcessingSpecialist?.isDirty || !editTimeProcessingSpecialist?.value || false}
          showCheckbox
          checkbox={
            <ContactCheckbox
              contacts={timeProcessingSpecialist}
              editContacts={editTimeProcessingSpecialist}
              onValueChange={onCheckboxClick}
              disabled={editTimeProcessingSpecialist?.isDirty || !timeProcessingSpecialist?.email || false}
            />
          }
        />
      </List>
      <InternalContactSaveModal />
    </>
  );
};
